import { useExportApi } from '../UseExportApi';
import { ExportChartOptions } from '../Model';
import { useCallback } from 'react';
import { exportStackedChart } from './ExportStackedChart';
import { FactRevenue, Option, PageName } from '@utils/pageConfig/Types';
import { StackedChartResp } from '@data-visualization/stacked-chart/Types';
import { useGetLoadedCountries } from '../UseGetLoadedCountries';
import { useGetLoadedProductGroups } from '@hooks/export/UseGetLoadedProductGroups';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { CapturedImage } from '@ecosystem/newron-design-system/build/export-utils/captureImage';

export interface ExportStackedChartOptions extends ExportChartOptions {
  title: string;
  appliedCountry: Option[] | undefined;
  appliedPg: Option[] | undefined;
  data: StackedChartResp[];
  timePeriod: string;
  comparisonTimePeriod: string;
  factRevenue: FactRevenue;
  periodType: string;
  showPerformanceWithin: string;
  appliedFiltersText: string;
  selectedKpi: Option;
  segmentBy: string;
  volume: string;
  pageName: PageName;
  exportImage?: CapturedImage;
}

export function useExportStackedChart(): {
  download: (options: ExportStackedChartOptions) => Promise<void>;
} {
  const { exportFile: exportFn } = useExportApi();
  const pagesConfig = usePageConfig();
  const loadeCountries = useGetLoadedCountries();
  const { loadedProductGroups } = useGetLoadedProductGroups();

  const download = useCallback(
    async (options: ExportStackedChartOptions) => {
      const {
        exportFormats,
        invokeExport,
        title,
        appliedCountry,
        appliedPg,
        data,
        timePeriod,
        comparisonTimePeriod,
        factRevenue,
        periodType,
        showPerformanceWithin,
        appliedFiltersText,
        selectedKpi,
        segmentBy,
        volume,
        pageName,
        exportImage,
      } = options;

      await exportStackedChart({
        exportFn,
        exportFormats,
        invokeExport,
        title,
        appliedCountry,
        appliedPg,
        data,
        timePeriod,
        comparisonTimePeriod,
        factRevenue,
        periodType,
        showPerformanceWithin,
        appliedFiltersText,
        selectedKpi,
        segmentBy,
        pagesConfig,
        pageName,
        volume,
        exportImage,
        ...loadeCountries,
        ...loadedProductGroups,
      });
    },
    [pagesConfig, loadeCountries, loadedProductGroups],
  );

  return {
    download,
  };
}
